import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, KeyRound, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { changeEmail, changePassword, formatApiError } from "../lib/api";

const Field = ({ label, testid, ...props }) => (
  <label className="block text-xs font-mono uppercase tracking-[0.14em] text-slate-500">{label}
    <input data-testid={testid} {...props} required className="what-input mt-2 normal-case tracking-normal" />
  </label>
);

const useSubmit = (fn, onDone) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      onDone(await fn());
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setBusy(false);
  };
  return { busy, error, submit };
};

const Section = ({ icon: Icon, title, testid, form, children }) => (
  <form onSubmit={form.submit} data-testid={testid} className="what-tester-card space-y-4">
    <h2 className="font-display text-lg font-bold text-white flex items-center gap-2"><Icon size={17} className="text-[#D4FF3A]" /> {title}</h2>
    {children}
    {form.error && <p data-testid={`${testid}-error`} className="text-sm text-red-300">{form.error}</p>}
    <button data-testid={`${testid.replace(/-form$/, "")}-submit`} disabled={form.busy} className="what-btn-primary flex items-center gap-2">
      {form.busy && <Loader2 size={15} className="animate-spin" />} Save
    </button>
  </form>
);

const EmailForm = () => {
  const { user, setUser } = useAuth();
  const [v, setV] = useState({ new_email: "", current_password: "" });
  const form = useSubmit(() => changeEmail(v), (r) => {
    setUser(r.user);
    setV({ new_email: "", current_password: "" });
    toast.success("Email changed. Use it next time you log in.");
  });
  return (
    <Section icon={Mail} title="Change email" testid="change-email-form" form={form}>
      <p className="text-sm text-slate-400">Now: <span data-testid="account-current-email" className="text-white">{user.email}</span></p>
      <Field label="New email" testid="change-email-new" type="email" autoComplete="email" value={v.new_email} onChange={(e) => setV({ ...v, new_email: e.target.value })} />
      <Field label="Current password" testid="change-email-password" type="password" autoComplete="current-password" value={v.current_password} onChange={(e) => setV({ ...v, current_password: e.target.value })} />
    </Section>
  );
};

const PasswordForm = () => {
  const { setUser } = useAuth();
  const [v, setV] = useState({ current_password: "", new_password: "", confirm: "" });
  const form = useSubmit(async () => {
    if (v.new_password !== v.confirm) throw { response: { data: { detail: "The new passwords don't match" } } };
    return changePassword({ current_password: v.current_password, new_password: v.new_password });
  }, (r) => {
    setUser(r.user);
    setV({ current_password: "", new_password: "", confirm: "" });
    toast.success("Password changed. You've been logged out on your other devices.");
  });
  return (
    <Section icon={KeyRound} title="Change password" testid="change-password-form" form={form}>
      <Field label="Current password" testid="change-password-current" type="password" autoComplete="current-password" value={v.current_password} onChange={(e) => setV({ ...v, current_password: e.target.value })} />
      <Field label="New password (8+ characters)" testid="change-password-new" type="password" minLength={8} autoComplete="new-password" value={v.new_password} onChange={(e) => setV({ ...v, new_password: e.target.value })} />
      <Field label="Repeat new password" testid="change-password-confirm" type="password" minLength={8} autoComplete="new-password" value={v.confirm} onChange={(e) => setV({ ...v, confirm: e.target.value })} />
    </Section>
  );
};

const Note = ({ testid, children }) => <p data-testid={testid} className="what-tester-card text-sm text-slate-400">{children}</p>;

export default function AccountPage() {
  const { user } = useAuth();
  if (user.kids_mode) return <Navigate to="/" replace />;
  return (
    <div data-testid="account-page" className="min-h-screen bg-[#0B0F17] text-slate-300">
      <div className="max-w-2xl mx-auto px-6 sm:px-10 py-14 space-y-6">
        <Link to="/" data-testid="account-back" className="text-xs font-mono text-slate-500 hover:text-white flex items-center gap-1.5"><ArrowLeft size={13} /> WHAT?</Link>
        <h1 className="font-display text-4xl font-extrabold text-white">Account</h1>
        {user.role === "student" ? (
          <Note testid="account-student-note">Your login was made by your teacher or parent. Ask them if you need a new password.</Note>
        ) : !user.has_password ? (
          <Note testid="account-no-password-note">You sign in with Google or Apple, so there's no WHAT? password to change. To add one, log out and use "Forgot password" with {user.email}.</Note>
        ) : (
          <>
            <EmailForm />
            <PasswordForm />
          </>
        )}
        <a href="/delete-account" data-testid="account-delete-link" className="inline-block text-xs font-mono text-slate-500 hover:text-red-300">Delete account</a>
      </div>
    </div>
  );
}
